import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Plus, Edit, KeyRound, Pause, Play, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  city: '',
  address: '',
  phone: '',
  email: '',
  user_name: '',
  password: '',
  notes: '',
};

const SubagentsManagement = () => {
  const { user } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/subagents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تعذّر تحميل قائمة الوكلاء');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'super_admin' && user?.role !== 'general_accountant') return;
    fetchRows();
  }, [user, fetchRows]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      city: row.city || '',
      address: row.address || '',
      phone: row.phone || '',
      email: row.email || '',
      user_name: row.user?.name || '',
      password: '',
      notes: row.description || '',
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.city.trim()) {
      toast.error('الاسم والمدينة والبريد إلزامية');
      return;
    }
    if (!editing && (!form.password || form.password.length < 6)) {
      toast.error('كلمة السر إلزامية ولا تقل عن 6 أحرف');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editing) {
        const body = {
          name: form.name,
          city: form.city,
          address: form.address,
          phone: form.phone,
          email: form.email,
          notes: form.notes,
        };
        await axios.patch(`${API}/subagents/${editing.id}`, body, { headers });
        toast.success('✅ تم تحديث الوكيل');
      } else {
        await axios.post(`${API}/subagents`, form, { headers });
        toast.success('✅ تم إنشاء الوكيل بنجاح');
      }
      setShowForm(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تعذّر الحفظ');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      const token = localStorage.getItem('token');
      const url = row.user?.is_active
        ? `${API}/subagents/${row.id}/suspend`
        : `${API}/subagents/${row.id}/reactivate`;
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(row.user?.is_active ? 'تم تعليق الحساب' : 'تم تفعيل الحساب');
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التحديث');
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/subagents/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.message || 'تم الحذف');
      setDeleteTarget(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحذف');
    }
  };

  const doResetPw = async () => {
    if (!pwTarget) return;
    if (!pwValue || pwValue.length < 6) {
      toast.error('كلمة السر يجب ألا تقل عن 6 أحرف');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subagents/${pwTarget.id}/reset-password`,
        { password: pwValue },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('✅ تم تحديث كلمة السر');
      setPwTarget(null);
      setPwValue('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التحديث');
    }
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.name || '').toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q)
      || (r.city || '').toLowerCase().includes(q)
      || (r.phone || '').includes(q)
    );
  });

  if (user?.role !== 'super_admin' && user?.role !== 'general_accountant') {
    return (
      <div className="p-8 text-center text-gray-500">
        🔒 هذه الصفحة مخصصة للسوبر أدمن والمحاسب العام
      </div>
    );
  }

  const [diagEmail, setDiagEmail] = useState('');
  const [diagResult, setDiagResult] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const runDiagnose = async () => {
    if (!diagEmail.trim()) return;
    setDiagBusy(true);
    setDiagResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API}/subagents/diagnose/${encodeURIComponent(diagEmail.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDiagResult({ ok: true, data: r.data });
    } catch (e) {
      setDiagResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setDiagBusy(false);
    }
  };

  const convertAgency = async () => {
    if (!diagResult?.data?.agency?.id) return;
    if (!window.confirm(`تحويل «${diagResult.data.agency.name}» إلى وكيل مُناوِل B2B؟`)) return;
    setDiagBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/subagents/convert/${diagResult.data.agency.id}`,
        { is_subagent: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(r.data?.message || '✅ تم التحويل');
      // refresh diagnose
      await runDiagnose();
      fetchRows();
    } catch (e) {
      toast.error('فشل التحويل: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDiagBusy(false);
    }
  };

  return (
    <div className="p-6 space-y-6" dir="rtl" data-testid="subagents-management">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🤝 الوكلاء المُناوِلون</h1>
          <p className="text-sm text-gray-600 mt-1">
            وكالات شريكة خارجية تستخدم نفس المنصة لبيع التذاكر والرحلات، وتُحوّل
            المداخيل النقدية للإدارة العامة بنفس آلية الفروع الداخلية.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="subagent-add-btn"
          className="bg-brand-600 hover:bg-brand-700 text-white"
        >
          <Plus className="h-4 w-4 ml-2" /> إضافة وكيل جديد
        </Button>
      </div>

      {/* 🩺 Diagnostic / Convert tool */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="text-base text-amber-900">
            🩺 تشخيص & تحويل وكالة موجودة إلى B2B
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-amber-800">
            هل لديك موظف في وكالة B2B لكنه ما زال يرى واجهة الموردين؟ أدخل بريده الإلكتروني هنا
            للتشخيص الفوري، ثم اضغط «تحويل إلى B2B» لإصلاح الوكالة.
          </p>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <Label className="text-xs">بريد الموظف</Label>
              <Input
                dir="ltr"
                type="email"
                value={diagEmail}
                onChange={(e) => setDiagEmail(e.target.value)}
                placeholder="employee@example.com"
                data-testid="subagent-diag-email"
              />
            </div>
            <Button
              onClick={runDiagnose}
              disabled={diagBusy || !diagEmail.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="subagent-diag-run"
            >
              {diagBusy ? '⏳ جاري الفحص…' : '🔍 فحص'}
            </Button>
          </div>
          {diagResult && (
            <div
              className={`rounded-md p-3 border text-sm ${diagResult.ok ? 'bg-white border-amber-300' : 'bg-rose-50 border-rose-300'}`}
              data-testid="subagent-diag-result"
            >
              {diagResult.ok ? (
                <>
                  <div className="font-bold mb-2">{diagResult.data?.verdict}</div>
                  <div className="text-xs space-y-1">
                    <div>👤 المستخدم: <strong>{diagResult.data?.user?.name}</strong> ({diagResult.data?.user?.role})</div>
                    <div>🏢 الوكالة: <strong>{diagResult.data?.agency?.name || '—'}</strong></div>
                    <div>🤝 B2B: {diagResult.data?.agency?.is_subagent ? <span className="text-emerald-700 font-bold">نعم ✅</span> : <span className="text-rose-700 font-bold">لا ❌</span>}</div>
                  </div>
                  {(diagResult.data?.issues || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {diagResult.data.issues.map((iss, i) => (
                        <div key={i} className="text-xs text-rose-800 bg-rose-50 p-1.5 rounded">{iss}</div>
                      ))}
                    </div>
                  )}
                  {diagResult.data?.agency?.id && !diagResult.data?.agency?.is_subagent && (
                    <Button
                      onClick={convertAgency}
                      disabled={diagBusy}
                      className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="subagent-convert-btn"
                    >
                      🔄 تحويل «{diagResult.data.agency.name}» إلى B2B
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-rose-700 font-mono text-xs">{diagResult.error}</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>قائمة الوكلاء</CardTitle>
          <div className="relative w-72">
            <Search className="h-4 w-4 absolute right-3 top-3 text-gray-400" />
            <Input
              dir="rtl"
              placeholder="بحث بالاسم / المدينة / البريد"
              className="pr-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="subagent-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500">جاري التحميل…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              لا يوجد وكلاء مُناوِلون بعد. اضغط «إضافة وكيل جديد» لإنشاء أول حساب.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">الوكيل</TableHead>
                  <TableHead className="text-right">المدينة</TableHead>
                  <TableHead className="text-right">الهاتف</TableHead>
                  <TableHead className="text-right">البريد / الدخول</TableHead>
                  <TableHead className="text-right">الحالة</TableHead>
                  <TableHead className="text-right">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`subagent-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{r.city}</TableCell>
                    <TableCell className="font-mono text-xs">{r.phone || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell>
                      {r.user?.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                          نشط
                        </Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700 border border-rose-200">
                          مُعلَّق
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(r)}
                          data-testid={`subagent-edit-${r.id}`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPwTarget(r)}
                          data-testid={`subagent-pw-${r.id}`}
                          title="إعادة تعيين كلمة السر"
                        >
                          <KeyRound className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleActive(r)}
                          data-testid={`subagent-toggle-${r.id}`}
                          title={r.user?.is_active ? 'تعليق' : 'تفعيل'}
                        >
                          {r.user?.is_active ? (
                            <Pause className="h-3 w-3" />
                          ) : (
                            <Play className="h-3 w-3" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-rose-600 hover:bg-rose-50"
                          onClick={() => setDeleteTarget(r)}
                          data-testid={`subagent-del-${r.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? '✏️ تعديل وكيل' : '➕ إضافة وكيل مُناوِل جديد'}
            </DialogTitle>
            <DialogDescription>
              سيُنشأ حساب دخول مرتبط بالوكيل تلقائياً (يدخل بنفس صفحة تسجيل دخول
              المنصة). تظل الموردين والتكاليف وإعدادات الإدارة العامة مخفية عنه.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>اسم الوكالة *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="subagent-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>المدينة *</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="subagent-form-city"
                />
              </div>
              <div>
                <Label>الهاتف</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="subagent-form-phone"
                />
              </div>
            </div>
            <div>
              <Label>العنوان</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="subagent-form-address"
              />
            </div>
            <div>
              <Label>البريد الإلكتروني (يُستخدم لتسجيل الدخول) *</Label>
              <Input
                type="email"
                dir="ltr"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="subagent-form-email"
              />
            </div>
            {!editing && (
              <>
                <div>
                  <Label>اسم المستخدم (يظهر في الواجهة)</Label>
                  <Input
                    value={form.user_name}
                    placeholder="اختياري — يتم استخدام اسم الوكالة افتراضياً"
                    onChange={(e) => setForm({ ...form, user_name: e.target.value })}
                    data-testid="subagent-form-username"
                  />
                </div>
                <div>
                  <Label>كلمة السر *</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="subagent-form-password"
                  />
                </div>
              </>
            )}
            <div>
              <Label>ملاحظات</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="subagent-form-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>إلغاء</Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-brand-600 hover:bg-brand-700 text-white"
              data-testid="subagent-form-submit"
            >
              {submitting ? 'جاري الحفظ…' : editing ? 'حفظ التعديلات' : 'إنشاء الوكيل'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle>🔑 إعادة تعيين كلمة السر</DialogTitle>
            <DialogDescription>
              ستُستبدل كلمة سر حساب «{pwTarget?.name}» بقيمة جديدة فوراً.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            value={pwValue}
            onChange={(e) => setPwValue(e.target.value)}
            placeholder="كلمة السر الجديدة (≥ 6 أحرف)"
            data-testid="subagent-pw-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwTarget(null); setPwValue(''); }}>
              إلغاء
            </Button>
            <Button
              onClick={doResetPw}
              className="bg-brand-600 hover:bg-brand-700 text-white"
              data-testid="subagent-pw-submit"
            >
              تحديث
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle>🗑️ حذف الوكيل</DialogTitle>
            <DialogDescription>
              هل أنت متأكد من حذف «{deleteTarget?.name}»؟ إذا كانت هناك عمليات
              مرتبطة لن يُحذف نهائياً، سيتم تعليق الحساب فقط للحفاظ على السجلات.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>تراجع</Button>
            <Button
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="subagent-del-confirm"
            >
              تأكيد الحذف
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubagentsManagement;
